import {
  collection, collectionGroup, doc, addDoc, updateDoc, deleteDoc,
  getDoc, getDocs, query, where, orderBy, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';

const COLL = 'pitches';

export async function createPitch({ title, client, createdBy, userPrompt, slides }) {
  const shareToken = crypto.randomUUID();
  const ref = await addDoc(collection(db, COLL), {
    title:       title || 'Namnlös pitch',
    client:      client || '',
    createdBy,
    userPrompt:  userPrompt || '',
    slides:      slides || [],
    shareToken,
    shareEnabled: true,
    currentSlide: 0,
    createdAt:   serverTimestamp(),
    updatedAt:   serverTimestamp(),
  });
  return { id: ref.id, shareToken };
}

export async function updatePitch(pitchId, data) {
  await updateDoc(doc(db, COLL, pitchId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function getPitch(pitchId) {
  const snap = await getDoc(doc(db, COLL, pitchId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function listPitches(userId, { all = false } = {}) {
  // Hämtar alla pitchar (all=true) eller bara den inloggade användarens.
  // Sorteras client-side på createdAt desc.
  const q = all
    ? query(collection(db, COLL))
    : query(collection(db, COLL), where('createdBy', '==', userId));
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
}

export async function deletePitch(pitchId) {
  await deleteDoc(doc(db, COLL, pitchId));
}

export async function duplicatePitch(pitchId, createdBy) {
  const snap = await getDoc(doc(db, COLL, pitchId));
  if (!snap.exists()) throw new Error('Pitch finns inte');
  const src = snap.data();
  const shareToken = crypto.randomUUID();
  const ref = await addDoc(collection(db, COLL), {
    title:        `${src.title || 'Namnlös pitch'} (kopia)`,
    client:       src.client       || '',
    createdBy,
    userPrompt:   src.userPrompt   || '',
    slides:       src.slides       || [],
    slideTheme:   src.slideTheme   || {},
    departmentId: src.departmentId || '',
    shareToken,
    shareEnabled: true,
    currentSlide: 0,
    createdAt:    serverTimestamp(),
    updatedAt:    serverTimestamp(),
  });
  return { id: ref.id, shareToken };
}

export async function createExternalPitch({ title, client, createdBy, pitchType, externalFileUrl, externalStoragePath }) {
  const shareToken = crypto.randomUUID();
  const ref = await addDoc(collection(db, COLL), {
    title:               title || 'Namnlös presentation',
    client:              client || '',
    createdBy,
    pitchType,           // 'html' | 'pdf'
    externalFileUrl,
    externalStoragePath,
    slides:              [],
    shareToken,
    shareEnabled:        true,
    currentSlide:        0,
    createdAt:           serverTimestamp(),
    updatedAt:           serverTimestamp(),
  });
  return { id: ref.id, shareToken };
}

// ── AppLAB-delningar ──────────────────────────────────────────────────────────

export async function createApplabShare({ appId, appName, tagline, createdBy, ownerDisplayName, expiresAt }) {
  const shareToken = crypto.randomUUID();
  const ref = await addDoc(collection(db, COLL), {
    title:        appName || 'Namnlös app',
    client:       '',
    tagline:      tagline || '',
    createdBy,
    ownerDisplayName: ownerDisplayName || '',
    pitchType:    'applab_app',
    applab_ref: {
      app_id:      appId,
      snapshot_at: serverTimestamp(),
    },
    slides:       [],
    shareToken,
    shareEnabled: true,
    currentSlide: 0,
    expires_at:   expiresAt || null,
    createdAt:    serverTimestamp(),
    updatedAt:    serverTimestamp(),
  });
  return { pitchId: ref.id, shareToken };
}

export async function listApplabShares(appId) {
  const q = query(
    collection(db, COLL),
    where('pitchType', '==', 'applab_app'),
    where('applab_ref.app_id', '==', appId),
    orderBy('createdAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function deactivateShare(pitchId) {
  await updateDoc(doc(db, COLL, pitchId), { shareEnabled: false, updatedAt: serverTimestamp() });
}

export async function deactivateApplabShares(appId) {
  const q = query(
    collection(db, COLL),
    where('pitchType', '==', 'applab_app'),
    where('applab_ref.app_id', '==', appId),
    where('shareEnabled', '==', true),
  );
  const snap = await getDocs(q);
  if (snap.empty) return;
  const batch = writeBatch(db);
  snap.docs.forEach(d => batch.update(d.ref, { shareEnabled: false }));
  await batch.commit();
}

export async function getPitchByToken(shareToken) {
  const q = query(
    collection(db, COLL),
    where('shareToken', '==', shareToken),
    where('shareEnabled', '==', true),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d    = snap.docs[0];
  const data = d.data();
  // Kolla utgångsdatum
  if (data.expires_at && data.expires_at.toDate() < new Date()) return null;
  return { id: d.id, ...data };
}

// Hämta bakgrundsbilder från DokumentN (kategori "Bakgrundsbild")
export async function loadBackgrounds() {
  const q = query(
    collection(db, 'documentLibrary'),
    where('category', '==', 'Bakgrundsbild'),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => {
    const url     = d.data().currentFileUrl || '';
    const isVideo = /\.(mp4|mov|webm|ogg)(\?|#|$)/i.test(url);
    return {
      id:      d.data().name,
      label:   d.data().name,
      url,
      isVideo,
      theme:   d.data().note || 'dark',
    };
  });
}

// Hämta referensprojekt för AI-prompt och manuellt urval.
// Hämtar från referenceProjects-samlingen (samma som resten av appen).
export async function loadProjects() {
  const snap = await getDocs(collection(db, 'referenceProjects'));
  return snap.docs
    .map(d => mapProjectDoc(d))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sv'));
}

function mapProjectDoc(d) {
  const data = d.data();
  return {
    id:            d.id,
    projectNumber: data.projectNumber || data.number || '',
    name:          data.name || '',
    description:   data.projectDescription || data.description || '',
    role:          data.role || '',
    ourAssignment: data.ourAssignment || '',
    keywords:      data.keywords || [],
    budget:        data.budget || '',
    startDate:     data.startDate || '',
    endDate:       data.endDate || '',
    imageUrl:      data.imageUrl || data.image || '',
  };
}

// Ladda ett enskilt referensprojekt (för synkronisering i editor)
export async function loadOneProject(id) {
  const snap = await getDoc(doc(db, 'referenceProjects', id));
  if (!snap.exists()) return null;
  return mapProjectDoc(snap);
}

// ── Granskningskommentarer ────────────────────────────────────────────────────

const REVIEW_COLL = 'reviewComments';

// Hämtar antal "ny_kommentar"-kommentarer per pitch i ett enda anrop.
// Returnerar { [pitchId]: count }. Returnerar {} om indexet inte finns ännu.
export async function getNewCommentCounts() {
  try {
    const q = query(
      collectionGroup(db, REVIEW_COLL),
      where('beastStatus', '==', 'ny_kommentar'),
    );
    const snap = await getDocs(q);
    const counts = {};
    snap.docs.forEach(d => {
      const pitchId = d.ref.parent.parent.id;
      counts[pitchId] = (counts[pitchId] || 0) + 1;
    });
    return counts;
  } catch {
    return {};
  }
}

export async function getReviewComments(pitchId) {
  const q = query(
    collection(db, COLL, pitchId, REVIEW_COLL),
    orderBy('createdAt', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function addReviewComment(pitchId, { slideIndex, x, y, text, beastStatus, author, authorId }) {
  const ref = await addDoc(collection(db, COLL, pitchId, REVIEW_COLL), {
    slideIndex: slideIndex ?? null,
    x, y, text, beastStatus, author, authorId,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateReviewComment(pitchId, commentId, data) {
  await updateDoc(doc(db, COLL, pitchId, REVIEW_COLL, commentId), data);
}

export async function deleteReviewComment(pitchId, commentId) {
  await deleteDoc(doc(db, COLL, pitchId, REVIEW_COLL, commentId));
}

// ── Visningsstatistik ─────────────────────────────────────────────────────────

function detectDevice() {
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobil' : 'dator';
}

export async function logView(pitchId, totalSlides) {
  const ref = await addDoc(collection(db, COLL, pitchId, 'views'), {
    startedAt:    serverTimestamp(),
    device:       detectDevice(),
    referrer:     document.referrer || '',
    totalSlides,
    slidesReached: 0,
    completed:    false,
  });
  return ref.id;
}

export async function updateView(pitchId, viewId, slidesReached, totalSlides) {
  await updateDoc(doc(db, COLL, pitchId, 'views', viewId), {
    slidesReached,
    completed: slidesReached >= totalSlides - 1,
    lastSeen:  serverTimestamp(),
  });
}

export async function getViews(pitchId) {
  const q = query(collection(db, COLL, pitchId, 'views'), orderBy('startedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
